import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import ClientHeader from "./components/ClientHeader";
import ClientOverview from "./components/ClientOverview";
import ClientStats from "./components/ClientStats";
import ClientNotes from "./components/ClientNotes";
import ClientSchedule from "./components/ClientSchedule";
import Upload from "./components/Upload";
import ReceiptBuilderModal from "components/Receipts/ReceiptBuilderModal";
import { updateClient, getClient, addClientInstallment } from "./clientApi";
import InstallmentTable from "components/Installments/InstallmentTable";
import { buildApiUrl } from "../../../utils/api";
import { getToken } from "../../../utils/auth";

const ClientProfileOverview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Get client ID from URL params or location state
  const clientIdFromState = location.state?.client?.id || location.state?.client?._id || location.state?.clientId || location.state?.lead?.id || location.state?.lead?._id;
  const clientIdFromParams = params.clientId;
  const clientId = clientIdFromParams || clientIdFromState;
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!clientId);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const saveClientSection = async (sectionData, sectionName = "Client") => {
    if (!clientId) {
      showToast("Unable to identify this client.", "error");
      throw new Error("Unable to identify client");
    }

    const payload = new FormData();
    const excludeFields = ["profileImagePreview"];

    const getCurrentValue = (key) => {
      if (key === "folderId") {
        return client?.folderId?._id || client?.folderId || "";
      }
      return client?.[key];
    };

    Object.entries(sectionData).forEach(([key, value]) => {
      if (excludeFields.includes(key)) return;
      if (key === "profileImage" && value instanceof File) {
        payload.append("profileImage", value);
        return;
      }
      if (key === "folderId") {
        const currentValue = getCurrentValue(key);
        if (value === undefined || value === null || value === "") {
          if (currentValue) {
            payload.append("folderId", "");
          }
          return;
        }
        if (String(value) === String(currentValue)) return;
        payload.append("folderId", value);
        return;
      }

      if (value === undefined || value === null || value === "") return;
      if (Array.isArray(value) && value.length === 0) return;
      if (value && typeof value === "object" && !(value instanceof File) && Object.keys(value).length === 0) return;

      const currentValue = getCurrentValue(key);
      if (currentValue !== undefined && currentValue !== null && String(value) === String(currentValue)) return;

      payload.append(key, value);
    });

    if ([...payload.keys()].length === 0) {
      showToast(`No changes detected for ${sectionName}.`, "success");
      return client;
    }

    try {
      await updateClient(clientId, payload);
      const response = await getClient(clientId);
      const updatedClientData = response?.data || response;
      setClient(updatedClientData);
      showToast(`${sectionName} updated successfully`, "success");
      return updatedClientData;
    } catch (error) {
      console.error("Error saving client section:", error);
      const responseData = error?.response?.data;
      let errorMessage = "Failed to save client details";

      if (responseData?.errors && Array.isArray(responseData.errors)) {
        const validationMessages = responseData.errors
          .map((err) => `${err.path}: ${err.msg}`)
          .join("\n");
        errorMessage = validationMessages;
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, "error");
      throw error;
    }
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.show]);

  // Fetch client details from API
  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!clientId) {
        setFetching(false);
        return;
      }

      setFetching(true);
      try {
        const response = await getClient(clientId);
        const clientData = response?.data || response;
        setClient(clientData);
      } catch (error) {
        console.error("Error fetching client details:", error);
        showToast(
          error?.response?.data?.message || error.message || "Failed to fetch client details",
          "error"
        );
        // Fall back to location state if API fails
        if (location.state?.client || location.state?.lead) {
          setClient(location.state?.client || location.state?.lead);
        }
      } finally {
        setFetching(false);
      }
    };

    fetchClientDetails();
  }, [clientId, location.state?.client, location.state?.lead]);

  const handleAddInstallment = async (installmentData) => {
    try {
      const result = await addClientInstallment(client._id || clientId, installmentData);
      if (result.success) {
        showToast("Installment added successfully", "success");
        // Refetch client details
        const response = await getClient(clientId);
        setClient(response?.data || response);
      } else {
        showToast(result.message || "Failed to add installment", "error");
      }
    } catch (error) {
      showToast(error.message || "Failed to add installment", "error");
    }
  };

  return (
    <div className="flex w-full flex-col gap-5 overflow-visible">
      {fetching && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500"></div>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading client details...</p>
          </div>
        </div>
      )}

      {!fetching && !client && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-lg text-red-600 dark:text-red-400">Failed to load client details</p>
            <button
              onClick={() => navigate("/admin/client-management")}
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
            >
              <IoArrowBack className="text-base" />
              Back to Management
            </button>
          </div>
        </div>
      )}

      {!fetching && client && (
        <>
          {toast.show && (
            <div
              className={`fixed right-6 top-20 z-50 max-w-md rounded-2xl px-4 py-3 text-sm font-semibold shadow-xl backdrop-blur-sm ${toast.type === "error"
                  ? "border border-red-400/70 bg-red-500/20 text-red-700"
                  : "border border-green-400/70 bg-green-500/20 text-green-700"
                }`}
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {toast.message}
            </div>
          )}

          <div className="mt-5 flex flex-col gap-4 rounded-[24px] border border-gray-200 bg-white px-6 py-5 shadow-sm dark:border-white/10 dark:bg-navy-800 sm:flex-row sm:items-center sm:justify-between">
            {/* <div className="flex items-center justify-between gap-4"> */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(location.state?.from || "/admin/client-management")}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-gray-200 text-navy-700 transition hover:border-brand-500 hover:text-brand-500 dark:border-white/10 dark:text-white dark:hover:text-brand-300"
              >
                <IoArrowBack className="text-lg" />
              </button>
              <div>
                <p className="text-sm uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">Client Profile</p>
                <h1 className="text-2xl font-bold text-navy-900 dark:text-white">Client Management</h1>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0 ml-auto">
              <button
                onClick={() => setIsReceiptModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 whitespace-nowrap"
              >
                Generate Receipt
              </button>
             
              <button
                onClick={async () => {
                  if (!client?._id && !clientId) return;
                  setLoading(true);
                  try {
                    const token = getToken();
                    const res = await fetch(buildApiUrl("/leads/change-status"), {
                      method: "PUT",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: token ? `Bearer ${token}` : undefined,
                      },
                      body: JSON.stringify({ leadId: client?._id || clientId, newStatus: "Qualified" })
                    });
                    if (!res.ok) throw new Error("Failed to change client to lead");
                    showToast("Client changed to lead!", "success");
                    setTimeout(() => {
                      navigate(
                        `/admin/lead-management/lead-profile/${client?._id || clientId}`,
                        {
                          state: {
                            from: "/admin/lead-management",
                            lead: { ...client, _id: client?._id || clientId },
                          },
                        }
                      );
                    }, 500);
                  } catch (e) {
                    showToast("Failed to change client to lead", "error");
                  } finally {
                    setLoading(false);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-600 whitespace-nowrap"
                disabled={loading}
              >
                Change to Lead
              </button>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-12">
            <div className="col-span-12">
              <ClientHeader client={client} saveClientSection={saveClientSection} />
            </div>

            <div className="col-span-12">
              <ClientOverview client={client} saveClientSection={saveClientSection} />
            </div>

            <div className="col-span-12">
              <div className="grid gap-5 lg:grid-cols-2">
                <ClientNotes client={client} />
                <Upload client={client} setClient={setClient} />
              </div>
            </div>

            <div className="col-span-12">
              <ClientSchedule client={client} showToast={showToast} setClient={setClient} />
            </div>

            <div className="col-span-12">
              <ClientStats client={client} saveClientSection={saveClientSection} />
            </div>

            <div className="col-span-12">
              <InstallmentTable
                installments={client?.installments}
                onAddInstallment={handleAddInstallment}
                canUpdate={true} // Modify with proper permission check if available for client
              />
            </div>
          </div>

          <ReceiptBuilderModal
            isOpen={isReceiptModalOpen}
            onClose={() => setIsReceiptModalOpen(false)}
            client={client}
          />
        </>
      )}
    </div>
  );
};

export default ClientProfileOverview;
